/**
 * Client-side dedupe for person-property writes. Every `people.set` /
 * `people.set_once` call becomes a billed `$set` event even when nothing
 * changed; on 2026-09-28 that was 39% of all prod events (about 27 per user),
 * mostly the plan enrichment re-running on each profile reload and
 * first-visit milestones re-sent on every page mount.
 *
 * Each record is ONE bounded localStorage key scoped to a single user, so a
 * shared browser only ever holds the current user's record. Storage failures
 * fail open: the write is sent, as it was before this existed.
 */

const PERSON_KEY = 'ett:ph-person-sent'
const MILESTONE_KEY = 'ett:ph-milestones-sent'
/** Well above the ~20 milestone names in use; keeps the key bounded. */
const MAX_MILESTONES = 64

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota or blocked storage: the next call simply sends again.
  }
}

/** True when `props` differs from what was last sent for `userId`. Records it when so. */
export function personPropsChanged(userId: string, props: Record<string, unknown>): boolean {
  const serialized = JSON.stringify(props)
  const last = read<{ id: string; props: string }>(PERSON_KEY)
  if (last != null && last.id === userId && last.props === serialized) return false
  write(PERSON_KEY, { id: userId, props: serialized })
  return true
}

/** True the first time `name` is seen for `userId` in this browser. Records it when so. */
export function milestoneIsNew(userId: string, name: string): boolean {
  const last = read<{ id: string; names: string[] }>(MILESTONE_KEY)
  const names = last != null && last.id === userId && Array.isArray(last.names) ? last.names : []
  if (names.includes(name)) return false
  write(MILESTONE_KEY, { id: userId, names: [...names, name].slice(-MAX_MILESTONES) })
  return true
}
